import type { StartupPosePolicy } from "../../physics/startupPosePolicy";
import type { AmbiguousGeomRoleSelections } from "../../core/mjcfAmbiguousGeomRoles";
import type { Joint } from "../../types/scene";

interface ScenePreset {
  name?: string;
  description?: string;
  files: string[];
  /** If set, after loading the preset XML+files, load this gripper and attach by attachment_site. */
  gripperFolder?: string;
  gripperXml?: string;
}

export type PresetMap = Record<string, ScenePreset>;
export type FileMap = Record<string, File>;
export type KeyframeMap = Record<string, number[]>;
export type KeyframeDetailsMap = Record<
  string,
  {
    qpos: number[];
    ctrl?: number[];
    mpos?: number[];
    mquat?: number[];
    act?: number[];
  }
>;
export type PreservedMjcfSections = {
  custom?: string;
  extension?: string;
  lights?: string[];
};

export type PendingMjcfImportWarnings = {
  fileName: string;
  warnings: string[];
} | null;

export type PendingImportWarningOptions = {
  ambiguousGeomRoles?: AmbiguousGeomRoleSelections;
  rawMeshUnitScale?: "meters" | "millimeters";
};

/** Options for `handleKeyframeSelect`. */
export type HandleKeyframeSelectOptions = {
  /**
   * When true (default), floating-root keyframes only update hinge/slide values — the instance
   * keeps its current world position/orientation. Set false when applying XML home placement (spawn, recipe).
   */
  preserveWorldRootPose?: boolean;
};
export type MeshLoadingStates = Record<string, boolean>;
export type FileInputChangeEvent = { target: { files: FileList | File[]; value: string } };

export type KeyframeApplyResult = {
  success: boolean;
  appliedValues?: Record<string, number>;
  warnings?: string[];
  errors?: string[];
  structure?: string;
  transformation?: string | null;
};

export type QposStructure = {
  pattern: { baseValues: number; description: string };
  baseValues: number;
  jointValues: number[];
  totalJoints: number;
  mappingJoints?: Joint[];
};

export interface FileStoreState {
  // File state
  xmlFile: File | null;
  fileMap: FileMap;
  referencedFiles: string[];
  missingFiles: string[];
  keyframes: KeyframeMap;
  keyframeDetails: KeyframeDetailsMap;
  preservedMjcfSections: PreservedMjcfSections;
  exportWarnings: string[];
  pendingMjcfImportWarnings: PendingMjcfImportWarnings;

  // Loading states
  uploadingFiles: boolean;
  meshLoadingStates: MeshLoadingStates;
  processingMeshes: boolean;
  parsingXml: boolean;

  // Project state
  projectName: string;
  isNewProject: boolean;

  // Preset state
  presets: PresetMap;
  assetFolders: string[];

  // Dynamic loading state
  currentLoadingFolder?: string;
  loadingPreset?: boolean;
  loadingProgress?: number;
  loadingMessage?: string;
  loadingPhase?: string;
}

export interface FileStoreActions {
  // File actions
  setXmlFile: (file: File | null) => void;
  setFileMap: (fileMap: FileMap) => void;

  // Loading state actions
  setUploadingFiles: (uploading: boolean) => void;
  setMeshLoadingState: (meshName: string, loading: boolean) => void;
  setProcessingMeshes: (processing: boolean) => void;
  setParsingXml: (parsing: boolean) => void;

  // Project actions
  createNewProject: (projectName?: string, createEmptyXml?: boolean) => Promise<void>;
  setProjectName: (name: string) => void;
  isCurrentProjectNew: () => boolean;

  // XML generation
  generateXmlFromState: (options?: {
    startupPosePolicy?: StartupPosePolicy;
    /** Controls body/joint naming in generated XML. "uuid" is safer for runtime mappings; "readable" is user-facing. */
    xmlNamingMode?: "readable" | "uuid";
    /** When "initKeyframe", robot qpos comes from fileStore keyframes["init"]; object bodies use current scene. Ensures warmstart matches XML design pose. */
    keyframeSource?: "currentScene" | "initKeyframe";
    /** Body UUIDs treated as objects (e.g. pick/drop targets); their keyframe qpos comes from scene. Only used when keyframeSource === "initKeyframe". */
    objectBodyUuids?: string[];
    /** When set, keyframe qpos is built from this snapshot instead of getSceneState(). Used by warmstart so the worker is seeded with the restored pose even if IK/store updates race before XML is generated. */
    overrideSceneStateForKeyframe?: Record<string, unknown>;
    /** Keyframes to omit after the user chooses to skip invalid export keyframes. */
    skipKeyframeNames?: string[];
  }) => Promise<string>;
  saveCurrentStateAsXml: (filename?: string) => Promise<File>;
  saveXmlWithUpdates: () => Promise<void>;
  saveXmlAs: () => Promise<void>;

  // File handling
  handleFiles: (event: FileInputChangeEvent) => Promise<void>;
  proceedWithRegisteredMissingAssets: () => Promise<void>;
  proceedWithoutMissingAssets: () => Promise<void>;
  cancelMissingUploadDependencies: () => void;

  // Preset actions
  setPresets: (presets: PresetMap) => void;
  loadPresets: () => Promise<void>;
  loadPresetFolder: (folder: string, options?: { forceReload?: boolean }) => Promise<void>;

  // XML parsing
  parseXmlFile: (
    xmlFile: File,
    fileMap: FileMap,
    options?: { worldBodyNameBase?: string; meshLibraryPathPrefix?: string }
  ) => Promise<void>;
  continuePendingMjcfImportWithWarnings: (options?: PendingImportWarningOptions) => Promise<void>;
  cancelPendingMjcfImportWithWarnings: () => void;
  promptPendingMjcfImportWarnings: (args: {
    fileName: string;
    warnings: string[];
    onContinue: (options?: PendingImportWarningOptions) => Promise<void>;
    onCancel?: () => void;
  }) => void;

  // Keyframe actions
  clearAllKeyframes: () => void;
  mergeKeyframes: (newKeyframes: KeyframeMap) => void;
  handleKeyframeSelect: (
    keyframeName: string,
    ownerWorldBodyUuid?: string,
    options?: HandleKeyframeSelectOptions
  ) => Promise<void>;
  createKeyframe: (keyframeName: string, ownerWorldBodyUuid?: string) => Promise<void>;
  deleteKeyframe: (keyframeName: string) => Promise<void>;
  interpolateKeyframes: (
    keyframeName1: string,
    keyframeName2: string,
    factor: number,
    ownerWorldBodyUuid?: string
  ) => Promise<void>;

  // Reset
  resetFiles: () => void;
}

export type FileStoreSlice = FileStoreState & { actions: FileStoreActions };

export type FileStoreSet = (
  partial: Partial<FileStoreSlice> | ((state: FileStoreSlice) => Partial<FileStoreSlice>)
) => void;

export type FileStoreGet = () => FileStoreSlice;

export const createInitialFileState = (): FileStoreState => ({
  xmlFile: null,
  fileMap: {},
  referencedFiles: [],
  missingFiles: [],
  keyframes: {},
  keyframeDetails: {},
  preservedMjcfSections: {},
  exportWarnings: [],
  pendingMjcfImportWarnings: null,
  uploadingFiles: false,
  meshLoadingStates: {},
  processingMeshes: false,
  parsingXml: false,
  projectName: "Untitled Scene",
  isNewProject: true,
  presets: {},
  assetFolders: [],
});
